import { CssBaseline } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import AdminPanel from './components/AdminPanel';
import Auth from './components/Auth';
import CameraScreen from './components/CameraScreen';
import ResponsiveDrawer from './components/ResponsiveDrawer';
import { supabase } from './utils/supabase';
import tinycolor from 'tinycolor2';
import { Brand } from './types';

export default function App() {
    const [session, setSession] = useState<any>(null);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [brand, setBrand] = useState<Brand | null>(null);

    // falls back to some default until loaded
    const brandColor = brand?.color ?? '#0994b1';

    // Compute contrast text color based on your brand color for legibility.
    const primaryContrastText = tinycolor(brandColor).isLight() ? '#000000' : '#ffffff';

    // Using tinycolor2 to generate related colors for the theme.
    const theme = createTheme({
        palette: {
            primary: {
                main: brandColor,
                contrastText: primaryContrastText,
            },
            background: {
                // default: tinycolor(brandColor).toString(), // main background
                // paper: tinycolor(brandColor).lighten(45).toString(),   // paper background (e.g., in Cards, Modal, etc.)
            },
            text: {
                primary: tinycolor(brandColor).darken(10).toString(),
                secondary: tinycolor(brandColor).darken(10).toString(),
            },
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: brandColor,
                        color: primaryContrastText,
                    },
                },
            },
            MuiListItemIcon: {
                styleOverrides: {
                    root: {
                        color: 'inherit', // Ensures icons take on parent text color
                    },
                },
            },
        },
    });


    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            if (session) {
                checkUserRole(session.user.id);
            }
        });

        supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            if (session) {
                checkUserRole(session.user.id);
            } else {
                setIsAdmin(false);
            }
        });

        const fetchBrand = async () => {
            const { data, error } = await supabase
                .from('brand')
                .select('id,color')
                .single();
            if (error) console.error('fetchBrand error', error);
            else setBrand(data);
        };

        fetchBrand();
    }, []);

    async function checkUserRole(userId: string) {
        const { data, error } = await supabase
            .from('users')
            .select('role')
            .eq('id', userId)
            .single();
        if (data && !error) {
            setIsAdmin(data.role === 'admin');
        }
    }

    useEffect(() => {
        if (!session) return;

        const channel = supabase
            .channel('public:users')
            .on(
                'postgres_changes',
                {
                    event: 'DELETE',
                    schema: 'public',
                    table: 'users',
                    filter: `id=eq.${session.user.id}`,
                },
                (payload) => {
                    console.log('User record deleted. Forcing logout:', payload);
                    supabase.auth.signOut();
                }
            )
            .subscribe();

        return () => {
            // Clean up by removing the channel subscription on unmount or session change
            supabase.removeChannel(channel);
        };
    }, [session]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                {session ? (
                    // Wrap authenticated routes with the responsive drawer
                    <ResponsiveDrawer isAdmin={isAdmin} session={session}>
                        <Routes>
                            <Route path="/admin" element={isAdmin ? <AdminPanel /> : <Navigate to="/login" />} />
                            <Route path="/camera" element={<CameraScreen userId={session.user.id} isAdmin={isAdmin} />} />
                            <Route path="*" element={<Navigate to="/camera" />} />
                        </Routes>
                    </ResponsiveDrawer>
                ) : (
                    <Routes>
                        <Route path="/login" element={<div style={{ minHeight: '100vh', backgroundColor: brandColor }}><Auth /></div>} />
                        <Route path="*" element={<Navigate to="/login" />} />
                    </Routes>
                )}
            </Router>
        </ThemeProvider>);
}